import React, { useState } from 'react'
import BackArrowIcon from '@qirapagos/web/src/assets/svg/BackArrow'
import { useFormik } from 'formik'
import { useTable } from 'hooks/useTable'
import { formatDateForFilter } from '@qirapagos/lib/utils/common'
import { FilterType } from 'constants/filters'
import {
  Drawer,
  Title,
  HeaderContainer,
  DrawerContainer,
  BackButton,
  ContentContainer,
  Button,
  ButtonsContainer
} from './styles'
import { Drawers } from 'store/drawer/types'
import { DatePickerField } from 'components/shared/DatePickerField'
import wrapper from 'components/organisms/Drawers/wrapper'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { getFilteredTransfers } from 'store/transfers/thunks'
import { downloadSummaryMovements } from 'store/investments/thunks'
import { format } from 'date-fns'
import { useAppSelector } from 'hooks/redux'

export interface FormValues {
  dateFrom: Date | null | undefined
  dateTo: Date | null | undefined
}

interface Props {
  showDrawer: boolean
  onClose: () => void
}

const DrawerFilterDate: React.FC<Props> = ({ showDrawer, onClose }) => {
  const {
    handleDateFilterType,
    handleDatesToFilter,
    datesToFilter,
    restartDateFilterCb
  } = useTable()

  const dispatch = useDispatch()
  const { data } = useAppSelector(state => state.drawer)
  const { isLoading } = useAppSelector(state => state.investments)
  const { fromInvestmentSummary } = data[Drawers.FILTER_DATE] as any

  const onSubmit = ({ dateFrom, dateTo }: FormValues) => {
    if (dateFrom && dateTo) {
      handleDateFilterType(
        `${formatDateForFilter(new Date(dateFrom))} - ${formatDateForFilter(
          new Date(dateTo)
        )}`
      )
      handleDatesToFilter(dateFrom, dateTo)
      dispatch(getFilteredTransfers(dateFrom, dateTo))
      onClose()
    }
  }

  const formik = useFormik({
    initialValues: {
      dateFrom: datesToFilter.dateFrom,
      dateTo: datesToFilter.dateTo
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      dateFrom: Yup.date()
        .typeError('Fecha inválida, por favor ingresá una fecha válida')
        .required('Fecha desde es requerida'),
      dateTo: Yup.date()
        .typeError('Fecha inválida, por favor ingresá una fecha válida')
        .when(
          'dateFrom',
          (dateFrom: Date, schema: Yup.DateSchema<Date | null>) => {
            return dateFrom instanceof Date && !isNaN(dateFrom.getTime())
              ? schema.min(
                dateFrom,
                'La fecha hasta debe ser mayor a la fecha desde'
              )
              : schema
          }
        )
        .required('Fecha hasta es requerida')
        .nullable(true)
    }),
    onSubmit
  })
  const resetFilter = () => {
    handleDateFilterType(FilterType.ALL_DATES)
    handleDatesToFilter(null, null)
    restartDateFilterCb?.()
    onClose()
  }

  const downloadPdf = async (dates: { dateFrom: string; dateTo: string }) => {
    try {
      const response: any = await dispatch(downloadSummaryMovements(dates))
      const base64Data = response

      if (!base64Data) {
        console.error('No se recibió el PDF desde el servidor')
        return
      }

      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'summary-movements.pdf'
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      onClose()
    } catch (error) {
      console.error('Error al descargar el PDF:', error)
    }
  }

  const handleDownloadClick = () => {
    const { dateFrom, dateTo } = formik.values
    if (dateFrom && dateTo) {
      const formattedDates = {
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd')
      }
      downloadPdf(formattedDates)
    }
  }

  return (
    <Drawer
      open={showDrawer}
      onClose={() => !isLoading && onClose()}
      direction="right"
    >
      <DrawerContainer>
        <HeaderContainer>
          <BackButton onPress={() => !isLoading && onClose()}>
            <BackArrowIcon />
          </BackButton>
          <Title>
            {fromInvestmentSummary
              ? 'Resumen de Inversiones'
              : 'Filtrar por fecha'}
          </Title>
        </HeaderContainer>
        <ContentContainer>
          <DatePickerField formik={formik} name="dateFrom" label="Desde" />
          <DatePickerField formik={formik} name="dateTo" label="Hasta" />
        </ContentContainer>

        <ButtonsContainer>
          {fromInvestmentSummary
            ? (
              <>
                <Button loading={isLoading} onPress={handleDownloadClick}>
                  Descargar PDF
                </Button>
                <Button disabled={isLoading} onPress={onClose} secondary>
                  Volver a inversiones
                </Button>
              </>
              )
            : (
              <>
                <Button onPress={formik.handleSubmit}>Aplicar Filtro</Button>
                <Button onPress={resetFilter} secondary>
                  Reiniciar Filtro
                </Button>
              </>
              )}
        </ButtonsContainer>
      </DrawerContainer>
    </Drawer>
  )
}

export default wrapper(DrawerFilterDate, Drawers.FILTER_DATE)
